import { Link } from "react-router-dom";
import { useTheme } from "../../../contexts/ThemeContext";

export default function DashboardSection() {
  const { isDark } = useTheme();

  return (
    <section className={`py-28 px-8 lg:px-16 transition-colors duration-300 ${isDark ? "bg-[#080808]" : "bg-[#f8f8f8]"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Visual side */}
          <div className="relative order-2 lg:order-1">
            <div className={`border rounded-2xl p-6 overflow-hidden ${isDark ? "bg-[#0d0d0d] border-white/8" : "bg-white border-black/8"}`}>
              <div className="flex items-center justify-between mb-6">
                <span className={`font-inter text-xs uppercase tracking-widest ${isDark ? "text-[#666666]" : "text-[#888888]"}`}>Margin Breakdown</span>
                <span className={`font-inter text-xs ${isDark ? "text-[#555555]" : "text-[#999999]"}`}>Last 30 days</span>
              </div>

              <div className="flex flex-col gap-3 mb-6">
                {[
                  { card: "2023 Topps Shohei Ohtani PSA 10", buy: "$38", sell: "$95", profit: "+$39.20", roi: "+103%", positive: true },
                  { card: "Nike Air Jordan 1 Retro High OG", buy: "$180", sell: "$340", profit: "+$121.50", roi: "+67%", positive: true },
                  { card: "Vintage Levi's 501 Denim Jacket", buy: "$22", sell: "$58", profit: "+$27.80", roi: "+126%", positive: true },
                  { card: "2020 Prizm Patrick Mahomes Silver", buy: "$65", sell: "$72", profit: "-$4.50", roi: "-7%", positive: false },
                ].map((flip) => (
                  <div
                    key={flip.card}
                    className={`flex items-center justify-between border rounded-xl px-4 py-3 ${isDark ? "bg-[#111111] border-white/4" : "bg-[#f5f5f5] border-black/5"}`}
                  >
                    <div className="min-w-0 flex-1 pr-4">
                      <p className={`font-inter text-xs truncate ${isDark ? "text-[#888888]" : "text-[#444444]"}`}>{flip.card}</p>
                      <p className={`font-inter text-xs mt-0.5 ${isDark ? "text-[#555555]" : "text-[#999999]"}`}>
                        Buy {flip.buy} → Sell {flip.sell}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className={`font-syne font-bold text-sm ${flip.positive ? (isDark ? "text-white" : "text-[#111111]") : (isDark ? "text-[#555555]" : "text-[#999999]")}`}>
                        {flip.profit}
                      </p>
                      <p className={`font-inter text-xs ${isDark ? "text-[#555555]" : "text-[#999999]"}`}>{flip.roi} ROI</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Total Flips", value: "18" },
                  { label: "Net Profit", value: "$623.40" },
                  { label: "Avg. ROI", value: "87%" },
                ].map((s) => (
                  <div key={s.label} className={`border rounded-xl p-3 text-center ${isDark ? "bg-white/4 border-white/6" : "bg-black/4 border-black/6"}`}>
                    <p className={`font-syne font-bold text-lg ${isDark ? "text-white" : "text-[#111111]"}`}>{s.value}</p>
                    <p className={`font-inter text-xs mt-0.5 ${isDark ? "text-[#666666]" : "text-[#777777]"}`}>{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Floating card */}
            <div className={`absolute -bottom-10 -right-4 border rounded-2xl p-4 ${isDark ? "bg-[#111111] border-white/10" : "bg-white border-black/10"}`}>
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${isDark ? "bg-white/5" : "bg-black/5"}`}>
                  <i className={`ri-alert-line text-sm ${isDark ? "text-[#888888]" : "text-[#555555]"}`} />
                </div>
                <div>
                  <p className={`font-syne font-bold text-base ${isDark ? "text-white" : "text-[#111111]"}`}>$142</p>
                  <p className={`font-inter text-xs ${isDark ? "text-[#666666]" : "text-[#777777]"}`}>hidden fees this month</p>
                </div>
              </div>
            </div>
          </div>

          {/* Text side */}
          <div className="order-1 lg:order-2">
            <span className={`font-inter text-xs uppercase tracking-[0.2em] block mb-5 ${isDark ? "text-[#666666]" : "text-[#888888]"}`}>
              Know Your Margins
            </span>
            <h2 className={`font-syne font-bold text-5xl lg:text-6xl leading-[1.05] mb-6 ${isDark ? "text-white" : "text-[#111111]"}`}>
              Your real profit <br />is hiding in the<br />
              <span className={`text-transparent bg-clip-text ${isDark ? "bg-gradient-to-r from-[#aaaaaa] to-[#444444]" : "bg-gradient-to-r from-[#555555] to-[#cccccc]"}`}>
                fees.
              </span>
            </h2>
            <p className={`font-inter text-base leading-relaxed mb-8 max-w-md ${isDark ? "text-[#555555]" : "text-[#666666]"}`}>
              Most resellers see their sale price and feel good. But after eBay takes 13.25%, PayPal takes 2.9%, and shipping eats another $8 — that &ldquo;$50 profit&rdquo; can quietly become $22. Trackly makes sure you always know the real number, no matter what you&apos;re selling.
            </p>

            <ul className="flex flex-col gap-4 mb-10">
              {[
                "Every platform fee auto-calculated — no manual math",
                "See profit per sale before you even list",
                "Understand exactly where your margin goes",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                    <i className={`ri-check-line text-sm ${isDark ? "text-[#888888]" : "text-[#555555]"}`} />
                  </div>
                  <span className={`font-inter text-sm leading-relaxed ${isDark ? "text-[#666666]" : "text-[#666666]"}`}>{item}</span>
                </li>
              ))}
            </ul>

            <Link to="/demonstration">
              <button className={`font-inter text-sm px-7 py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center gap-2 w-fit border ${
                isDark
                  ? "text-white border-white/15 hover:border-white/30 hover:bg-white/5"
                  : "text-[#111111] border-black/15 hover:border-black/30 hover:bg-black/5"
              }`}>
                See How It Works
                <i className="ri-arrow-right-line" />
              </button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
